import { useEffect, useLayoutEffect, useState, useRef, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Chip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

import { Charts } from "./Charts";
import { DailyCalendar } from "./DailyCalendar";
import SerchSideBar from "./SerchSideBar";

import { useAtomValue, useSetAtom } from "jotai";
import { AppAtoms } from "../lib/store";

import {
  getEntryAnalysys,
  getEntryAnalysysMultiDivision,
} from "../api/entryAnalysys";
import {
  getTossUpAnalysys,
  getTossUpAnalysysMultiDivision,
} from "../api/tossUpAnalysys";
import {
  getSerchProducts,
  getSerchHighRankCompanys,
  getSerchCategorys,
  getSerchSaleCompanys,
  getSerchSaleCompanyGroups,
  getSerchAccountStaffs,
  getSerchAccountSales,
} from "../api/serch";

import {
  entryAnalysysTableColumn,
  getEntryDataColumnForModal,
} from "../config/entryAnalysys";
import { tossUpAnalysysTableColumn } from "../config/tossUpAnalysys";
import { dataDifine } from "../config/dataInterface";
import { getFirstYear, getOneYear } from "../lib/other";

/** 表示区分名 → 検索キーのマッピング */
const DIVISION_TO_SEARCH_KEY: Record<string, string> = {
  // entry
  "商材": "product_name",
  "商材名": "product_name",
  "パートナー": "sale_company_name",
  "グループ": "sale_company_group_name",
  "営業アカウント": "sale_account_name",
  // tossup
  "トスアップパートナー": "toss_up_company_name",
  "トスアップグループ": "toss_up_company_group_name",
  "トスアップ担当者": "toss_up_account_name",
  // closing
  "クロージングパートナー": "closer_company_name",
  "クロージンググループ": "closer_company_group_name",
  "クロージング担当者": "closer_account_name",
  "クロージング商材": "closer_product_name",
};

const RANKING_COLORS = [
  "#FF4B00",
  "#005AFF",
  "#03AF7A",
  "#F6AA00",
  "#4DC4FF",
  "#990099",
  "#84919E",
];

/** 表示区分キーを type と authority から決定 */
function getDivisionKey(
  type: AnalysisDetailModalType,
  authority: string | undefined,
): string {
  if (type === "entry") {
    return authority === "パートナー" || authority === "営業アカウント"
      ? "entry_analysys_division_partner"
      : "entry_analysys_division_admin";
  }
  return authority === "パートナー" || authority === "営業アカウント"
    ? "toss_up_analysys_division_partner"
    : "toss_up_analysys_division_admin";
}

/** analysys 行のカテゴリ値を合計 */
function sumRow(row: dataDifine): number {
  let total = 0;
  for (const [key, val] of Object.entries(row)) {
    if (
      key === "name" ||
      key === "sub_count_column_name" ||
      key.endsWith("_sub_column")
    )
      continue;
    total += Number(val) || 0;
  }
  return total;
}

const buildEntrySerchColumn = (
  thisYear: number,
  thisMonth: number,
  lastYear: string,
  lastMonth: string,
): { [key: string]: string } => {
  const fromDataColumn = entryAnalysysTableColumn.dataColumn.reduce<{
    [key: string]: string;
  }>((acc, d) => {
    if (!d.serch) return acc;
    if (d.serchType === "analysys_year_month") {
      return {
        ...acc,
        analysys_granularity_month_start_year: lastYear,
        analysys_granularity_month_start_month: lastMonth,
        analysys_granularity_month_end_year: String(thisYear),
        analysys_granularity_month_end_month: String(thisMonth).padStart(
          2,
          "0",
        ),
        analysys_granularity_date_year: String(thisYear),
        analysys_granularity_date_month: String(thisMonth).padStart(2, "0"),
      };
    }
    if (d.key === "analysys_granularity") return { ...acc, [d.key]: "日別" };
    if (d.key === "entry_analysys_division_admin")
      return { ...acc, [d.key]: "商材,パートナー,営業アカウント" };
    if (d.key === "entry_analysys_division_partner")
      return { ...acc, [d.key]: "商材,営業アカウント" };
    if (d.key === "entry_analysys_count_type")
      return { ...acc, [d.key]: "システム登録日" };
    if (d.key === "entry_analysys_sub_count")
      return { ...acc, [d.key]: "クロージング件数" };
    if (d.key === "entry_analysys_price_type")
      return { ...acc, [d.key]: "ショット" };
    return { ...acc, [d.key]: "" };
  }, {});
  return {
    ...fromDataColumn,
    entry_aggregation_item: "エントリー数",
    entry_analysys_count_type: "システム登録日",
  };
};

const buildTossUpSerchColumn = (
  thisYear: number,
  thisMonth: number,
  lastYear: string,
  lastMonth: string,
): { [key: string]: string } => {
  const fromDataColumn = tossUpAnalysysTableColumn.dataColumn.reduce<{
    [key: string]: string;
  }>((acc, d) => {
    if (!d.serch) return acc;
    if (d.serchType === "analysys_year_month") {
      return {
        ...acc,
        analysys_granularity_month_start_year: lastYear,
        analysys_granularity_month_start_month: lastMonth,
        analysys_granularity_month_end_year: String(thisYear),
        analysys_granularity_month_end_month: String(thisMonth).padStart(
          2,
          "0",
        ),
        analysys_granularity_date_year: String(thisYear),
        analysys_granularity_date_month: String(thisMonth).padStart(2, "0"),
      };
    }
    if (d.key === "analysys_granularity") return { ...acc, [d.key]: "日別" };
    if (d.key === "toss_up_analysys_division_admin")
      return { ...acc, [d.key]: "トスアップパートナー,トスアップ担当者" };
    if (d.key === "toss_up_analysys_division_partner")
      return { ...acc, [d.key]: "トスアップグループ,トスアップ担当者" };
    if (d.key === "toss_up_aggregation_item")
      return { ...acc, [d.key]: "トスアップ数" };
    if (d.key === "toss_up_analysys_sub_count")
      return { ...acc, [d.key]: "クロージング-開通数" };
    return { ...acc, [d.key]: "" };
  }, {});
  return { ...fromDataColumn, toss_up_analysys_count_type: "トスアップ日" };
};

const buildClosingSerchColumn = (
  thisYear: number,
  thisMonth: number,
  lastYear: string,
  lastMonth: string,
): { [key: string]: string } => {
  const fromDataColumn = tossUpAnalysysTableColumn.dataColumn.reduce<{
    [key: string]: string;
  }>((acc, d) => {
    if (!d.serch) return acc;
    if (d.serchType === "analysys_year_month") {
      return {
        ...acc,
        analysys_granularity_month_start_year: lastYear,
        analysys_granularity_month_start_month: lastMonth,
        analysys_granularity_month_end_year: String(thisYear),
        analysys_granularity_month_end_month: String(thisMonth).padStart(
          2,
          "0",
        ),
        analysys_granularity_date_year: String(thisYear),
        analysys_granularity_date_month: String(thisMonth).padStart(2, "0"),
      };
    }
    if (d.key === "analysys_granularity") return { ...acc, [d.key]: "日別" };
    if (d.key === "toss_up_analysys_division_admin")
      return { ...acc, [d.key]: "クロージングパートナー,クロージング担当者" };
    if (d.key === "toss_up_analysys_division_partner")
      return { ...acc, [d.key]: "クロージンググループ,クロージング担当者" };
    if (d.key === "toss_up_aggregation_item")
      return { ...acc, [d.key]: "クロージング-エントリー数" };
    if (d.key === "toss_up_analysys_sub_count")
      return { ...acc, [d.key]: "クロージング-開通数" };
    return { ...acc, [d.key]: "" };
  }, {});
  return { ...fromDataColumn, toss_up_analysys_count_type: "クロージング日" };
};

export type AnalysisDetailModalType = "entry" | "tossup" | "closing";

interface AnalysisDetailModalProps {
  open: boolean;
  onClose: () => void;
  type: AnalysisDetailModalType;
  initialSearchParams?: { [key: string]: string } | null;
  initialData?: {
    analysys: dataDifine[];
    columns: string[];
    all_data: dataDifine[];
  } | null;
}

export function AnalysisDetailModal({
  open,
  onClose,
  type,
  initialSearchParams,
  initialData,
}: AnalysisDetailModalProps) {
  const setIsLoading = useSetAtom(AppAtoms.isLoading);
  const loginUser = useAtomValue(AppAtoms.loginUser);
  const isSmartPhone = useAtomValue(AppAtoms.isSmartPhone);
  const firstYear = getFirstYear();
  const { thisYear, thisMonth, lastYear, lastMonth } = getOneYear();
  const lastYearStr = String(lastYear);
  const lastMonthStr = String(lastMonth).padStart(2, "0");

  const tableColumn =
    type === "entry" ? entryAnalysysTableColumn : tossUpAnalysysTableColumn;
  const initialSerchColumn =
    type === "entry"
      ? buildEntrySerchColumn(thisYear, thisMonth, lastYearStr, lastMonthStr)
      : type === "closing"
        ? buildClosingSerchColumn(
            thisYear,
            thisMonth,
            lastYearStr,
            lastMonthStr,
          )
        : buildTossUpSerchColumn(
            thisYear,
            thisMonth,
            lastYearStr,
            lastMonthStr,
          );

  const [serchOpen, setSerchOpen] = useState(!isSmartPhone);
  const [serchString, setSerchString] = useState<{ [key: string]: string }>(
    initialSerchColumn,
  );
  const [serchStringView, setSerchStringView] = useState<{
    [key: string]: string;
  }>(initialSerchColumn);
  const [fetchData, setFetchData] = useState<dataDifine[]>([]);
  const [fetchAllData, setFetchAllData] = useState<dataDifine[]>([]);
  const [fetchColumn, setFetchColumn] = useState<string[]>([]);

  const [companyNameList, setCompanyNameList] = useState<string[]>([]);
  const [searchGroupNameList, setSearchGroupNameList] = useState<string[]>([]);
  const [highRankCompanyNameList, setHighRankCompanyNameList] = useState<
    string[]
  >([]);
  const [categoryNameList, setCategoryNameList] = useState<string[]>([]);
  const [productNameList, setProductNameList] = useState<string[]>([]);
  const [accountSaleNameList, setAccountSaleNameList] = useState<string[]>([]);
  const [accountStaffNameList, setAccountStaffNameList] = useState<string[]>(
    [],
  );

  const [multiDivisionData, setMultiDivisionData] = useState<Record<
    string,
    { analysys: dataDifine[]; columns: string[]; all_data: dataDifine[] }
  > | null>(null);

  const tableHeight = "560px";
  const [popperContainer, setPopperContainer] = useState<HTMLElement | null>(
    null,
  );
  const setPopperContainerRef = useCallback((el: HTMLDivElement | null) => {
    setPopperContainer((prev) => (prev === el ? prev : el));
  }, []);
  const skipNextSerchStringFetchRef = useRef(false);
  const prevOpenRef = useRef(false);
  const [chartLayoutReady, setChartLayoutReady] = useState(false);
  const [localLoading, setLocalLoading] = useState(false);

  const isDaily = (serchString.analysys_granularity || "日別") === "日別";
  const dk = getDivisionKey(type, loginUser?.db_authority);

  const activeCategories = useMemo(() => {
    return (serchString[dk] || "").split(",").filter(Boolean);
  }, [serchString, dk]);

  const hasMultiDivision = activeCategories.length > 0;

  // カレンダー用データ（日別合計）
  const calendarData = useMemo(() => {
    if (multiDivisionData) {
      const firstDiv = Object.keys(multiDivisionData)[0];
      if (firstDiv && multiDivisionData[firstDiv]?.analysys) {
        return multiDivisionData[firstDiv].analysys.map((row) => ({
          name: String(row.name || ""),
          合計: sumRow(row),
        }));
      }
    }
    if (fetchData.length > 0 && fetchColumn.length > 0) {
      return fetchData.map((row) => {
        let total = 0;
        for (const col of fetchColumn) {
          total += Number(row[col]) || 0;
        }
        return { name: String(row.name || ""), 合計: total };
      });
    }
    return [];
  }, [multiDivisionData, fetchData, fetchColumn]);

  async function getGraphData(edit: boolean, paramsOverride?: Record<string, string>) {
    if (!edit) {
      setIsLoading(true);
      setLocalLoading(true);
    }
    const params = paramsOverride ? { ...paramsOverride } : { ...serchString };
    const granularity = params.analysys_granularity || "日別";
    const divKey = getDivisionKey(type, loginUser?.db_authority);
    const divisions = (params[divKey] || "").split(",").filter(Boolean);

    if (divisions.length > 0) {
      // 複数区分一括取得
      const result =
        type === "entry"
          ? await getEntryAnalysysMultiDivision(params, divisions)
          : await getTossUpAnalysysMultiDivision(params, divisions);
      if (result) {
        setMultiDivisionData(result);
        const firstDiv = divisions[0];
        if (result[firstDiv]) {
          if (granularity === "月別") {
            // 月別: Charts用に合計値のみに集約
            const aggData = result[firstDiv].analysys.map(
              (row) =>
                ({ name: row.name, 合計: sumRow(row) }) as dataDifine,
            );
            setFetchData(aggData);
            setFetchColumn(["合計"]);
          } else {
            setFetchData(result[firstDiv].analysys);
            setFetchColumn(result[firstDiv].columns);
          }
          setFetchAllData(result[firstDiv].all_data);
        }
      } else {
        setMultiDivisionData(null);
        setFetchData([]);
        setFetchAllData([]);
        setFetchColumn([]);
      }
    } else {
      // 単一区分 or 未選択
      const result =
        type === "entry"
          ? await getEntryAnalysys(params)
          : await getTossUpAnalysys(params);
      if (result === null) {
        setFetchData([]);
        setFetchAllData([]);
        setFetchColumn([]);
      } else if (result) {
        setFetchData(result.analysys);
        setFetchAllData(result.all_data);
        setFetchColumn(result.columns);
      }
      setMultiDivisionData(null);
    }
    if (!edit) {
      setIsLoading(false);
      setLocalLoading(false);
    }
  }

  useLayoutEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    prevOpenRef.current = open;
    if (!justOpened) return;

    setSerchOpen(!isSmartPhone);

    const base =
      initialSearchParams && Object.keys(initialSearchParams).length > 0
        ? { ...initialSearchParams }
        : type === "entry"
          ? buildEntrySerchColumn(
              thisYear,
              thisMonth,
              lastYearStr,
              lastMonthStr,
            )
          : type === "closing"
            ? buildClosingSerchColumn(
                thisYear,
                thisMonth,
                lastYearStr,
                lastMonthStr,
              )
            : buildTossUpSerchColumn(
                thisYear,
                thisMonth,
                lastYearStr,
                lastMonthStr,
              );
    if (type === "entry") {
      if (!base.entry_aggregation_item) {
        base.entry_aggregation_item =
          base.entry_analysys_count_type === "開通日"
            ? "開通数"
            : base.entry_analysys_count_type === "解約日"
              ? "解約数"
              : "エントリー数";
      }
    }
    if (
      type === "closing" &&
      (!initialSearchParams || Object.keys(initialSearchParams).length === 0)
    ) {
      const useGroupDivision =
        loginUser?.db_authority === "パートナー" ||
        loginUser?.db_authority === "営業MGR";
      if (useGroupDivision) {
        base.toss_up_analysys_division_admin = "クロージンググループ";
        base.toss_up_analysys_division_partner = "クロージンググループ";
      }
    }
    setSerchString(base);
    setSerchStringView(base);
    if (initialData) {
      skipNextSerchStringFetchRef.current = true;
      setFetchData(initialData.analysys);
      setFetchAllData(initialData.all_data);
      setFetchColumn(initialData.columns);
      setMultiDivisionData(null);
    } else {
      skipNextSerchStringFetchRef.current = true;
      getGraphData(false, base);
    }
  }, [
    open,
    type,
    initialSearchParams,
    initialData,
    thisYear,
    thisMonth,
    lastYearStr,
    lastMonthStr,
    loginUser?.db_authority,
  ]);

  useEffect(() => {
    if (!open) {
      setChartLayoutReady(false);
      setFetchData([]);
      setFetchAllData([]);
      setFetchColumn([]);
      setMultiDivisionData(null);
      return;
    }
    // justOpened effect handles all fetch cases (initialData or getGraphData)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const rafId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!cancelled) setChartLayoutReady(true);
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (skipNextSerchStringFetchRef.current) {
      skipNextSerchStringFetchRef.current = false;
      return;
    }
    getGraphData(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch when user clicks 検索
  }, [serchString]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const [highRank, products, companies, groups, staffs, sales] =
        await Promise.all([
          getSerchHighRankCompanys(),
          getSerchProducts(),
          getSerchSaleCompanys(),
          getSerchSaleCompanyGroups(),
          getSerchAccountStaffs(),
          getSerchAccountSales(),
        ]);
      if (highRank?.highRankCompanys)
        setHighRankCompanyNameList([
          "",
          ...(
            highRank.highRankCompanys as { high_rank_company_name: string }[]
          ).map((c) => c.high_rank_company_name),
        ]);
      if (products?.products)
        setProductNameList([
          "",
          ...(products.products as { product_name: string }[]).map(
            (p) => p.product_name,
          ),
        ]);
      if (companies?.saleCompanys)
        setCompanyNameList([
          "",
          ...(companies.saleCompanys as { sale_company_name: string }[]).map(
            (c) => c.sale_company_name,
          ),
        ]);
      if (groups?.saleCompanyGroups)
        setSearchGroupNameList([
          "",
          ...(groups.saleCompanyGroups as { group_name: string }[]).map(
            (g) => g.group_name,
          ),
        ]);
      if (staffs?.accountStaffs)
        setAccountStaffNameList([
          "",
          ...(staffs.accountStaffs as { account_name: string }[]).map(
            (a) => a.account_name,
          ),
        ]);
      if (sales?.accountSales)
        setAccountSaleNameList([
          "",
          ...(sales.accountSales as { account_name: string }[]).map(
            (a) => a.account_name,
          ),
        ]);
    })();
    if (type === "tossup") {
      getSerchCategorys().then((res) => {
        if (res?.categorys)
          setCategoryNameList([
            "",
            ...(res.categorys as { category_name: string }[]).map(
              (c) => c.category_name,
            ),
          ]);
      });
    }
  }, [open, type]);

  const modalTitle =
    type === "entry"
      ? "エントリー分析"
      : type === "closing"
        ? "クロージング分析"
        : "トスアップ分析";
  const analysisSubType =
    type === "tossup" ? "tossup" : type === "closing" ? "closing" : undefined;

  /** ランキング部（全タイプ・全粒度共通） */
  const rankingSection = hasMultiDivision && (
    <>
      <Box
        sx={{ display: "flex", gap: 1, my: 2, alignItems: "center" }}
      >
        <Typography variant="body2" color="textSecondary">
          表示区分:
        </Typography>
        {activeCategories.map((cat) => (
          <Chip
            key={cat}
            label={cat}
            size="small"
            sx={{ bgcolor: "#1976d2", color: "white", fontWeight: 600 }}
          />
        ))}
      </Box>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        {activeCategories.map((cat, catIdx) => {
          const divData = multiDivisionData?.[cat];
          const allData =
            divData?.all_data || (catIdx === 0 ? fetchAllData : []);
          const chartH = Math.max(200, allData.length * 36 + 40);
          return (
            <Box key={cat} sx={{ flex: 1, minWidth: 280 }}>
              <Typography
                variant="subtitle2"
                fontWeight="bold"
                sx={{ mb: 1 }}
              >
                {cat}別ランキング
              </Typography>
              {allData.length === 0 ? (
                <Box
                  sx={{
                    height: 200,
                    border: "1px dashed #ccc",
                    borderRadius: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography variant="body2" color="textSecondary">
                    データなし
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ height: chartH }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      layout="vertical"
                      data={allData}
                      margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                    >
                      <XAxis type="number" />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={120}
                        tick={{ fontSize: 11 }}
                      />
                      <Tooltip />
                      <Bar
                        dataKey="合計"
                        cursor="pointer"
                        onClick={(data) => {
                          const searchKey = DIVISION_TO_SEARCH_KEY[cat];
                          if (searchKey && data?.name) {
                            setSerchString((prev) => ({
                              ...prev,
                              [searchKey]: String(data.name),
                            }));
                            setSerchStringView((prev) => ({
                              ...prev,
                              [searchKey]: String(data.name),
                            }));
                          }
                        }}
                      >
                        {allData.map((_, i) => (
                          <Cell
                            key={i}
                            fill={RANKING_COLORS[i % RANKING_COLORS.length]}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </>
  );

  return (
    <Dialog
      data-testid="analysis-detail-modal"
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      fullScreen={isSmartPhone}
      PaperProps={{
        sx: {
          ...(isSmartPhone
            ? {}
            : {
                maxHeight: "96vh",
                minHeight: "85vh",
                width: "95vw",
                maxWidth: 1600,
              }),
          visibility: localLoading ? "hidden" : "visible",
        },
      }}
      sx={{
        zIndex: 1400,
        "& .MuiBackdrop-root": {
          visibility: localLoading ? "hidden" : "visible",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span className="shippori-antique-regular">{modalTitle}</span>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <IconButton
            aria-label="toggle-search"
            onClick={() => setSerchOpen((prev) => !prev)}
            size="small"
            sx={{ color: serchOpen ? "primary.main" : "action.active" }}
          >
            <SearchIcon />
          </IconButton>
          <IconButton aria-label="close" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}
      >
        <Box
          ref={setPopperContainerRef}
          sx={{
            display: "flex",
            flexDirection: isSmartPhone ? "column" : "row",
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          {!(isSmartPhone && serchOpen) && (
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                pr: isSmartPhone ? 0 : 1,
              }}
            >
              {/* メインエリア（固定） */}
              <Box sx={{ flexShrink: 0 }}>
                {isDaily ? (
                  <>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      sx={{ mb: 1 }}
                    >
                      日別推移カレンダー
                    </Typography>
                    <DailyCalendar
                      year={parseInt(
                        serchString.analysys_granularity_date_year ||
                          String(thisYear),
                      )}
                      month={parseInt(
                        serchString.analysys_granularity_date_month ||
                          String(thisMonth),
                      )}
                      data={calendarData}
                      onPrev={() => {}}
                      onNext={() => {}}
                    />
                  </>
                ) : (
                  <Charts
                    key={chartLayoutReady ? "ready" : "initial"}
                    getGraphData={getGraphData}
                    fetchData={fetchData}
                    fetchAllData={hasMultiDivision ? [] : fetchAllData}
                    fetchColumn={fetchColumn}
                    tableHeight={tableHeight}
                    tableTitle={modalTitle}
                    hideTitleAndUpdate
                    verticalLayout
                  />
                )}
              </Box>
              {/* ランキングエリア（スクロール） */}
              <Box sx={{ flex: 1, overflow: "auto", minHeight: 0 }}>
                {rankingSection}
              </Box>
            </Box>
          )}
          {serchOpen && (
            <Box
              sx={{
                flexShrink: 0,
                flexGrow: isSmartPhone ? 1 : 0,
                width: isSmartPhone ? "100%" : 300,
                minHeight: 0,
                overflow: "hidden",
                borderLeft: isSmartPhone ? "none" : "1px solid #e0e0e0",
                pl: isSmartPhone ? 0 : 1,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <SerchSideBar
                setSerchOpen={setSerchOpen}
                tableHeight={tableHeight}
                tableColumn={tableColumn}
                dataColumn={
                  type === "entry" ? getEntryDataColumnForModal() : []
                }
                firstYear={firstYear}
                thisYear={thisYear}
                serchColumn={serchString}
                setSerchString={setSerchString}
                companyNameList={companyNameList}
                companyGroupNameList={searchGroupNameList}
                highRankCompanyNameList={highRankCompanyNameList}
                categoryNameList={categoryNameList}
                productNameList={productNameList}
                accountSaleNameList={accountSaleNameList}
                accountStaffNameList={accountStaffNameList}
                serchStringView={serchStringView}
                setSerchStringView={setSerchStringView}
                popperContainer={popperContainer}
                analysisSubType={analysisSubType}
                embeddedInModal
              />
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
